/**
 * Client Success — the wire contracts for Phase 1's two read-only views
 * (ADR-0077): the **Firms** roster + info panel, and the **Users** table.
 *
 * Read-only on the GET payloads. Pause/unpause and other write actions ship
 * as their own wire contracts (see FirmPauseResponseSchema in this file once
 * Phase 2 writes land).
 *
 * Both responses ship the full population in one payload — the scale is small
 * (tens of firms, ~120 users), so there is no server-side pagination: the SPA
 * does client-side search + the Originating/Handling filter toggle. Mirrors
 * what the portal does today.
 */

import { z } from "zod";
import { DashboardRowSchema } from "./dashboard.js";

/**
 * One file slot on a firm — either a present file (name + URL to the
 * attachment) or null (nothing on file). The panel uses null vs object as the
 * presence indicator that drives the at-a-glance ✓ / ✗ rendering.
 *
 * Airtable's Firms.Logo / W-9 / Malpractice / MFA fields are
 * `multipleAttachments` so the projection can hold history without a schema
 * change later; the panel surfaces only the FIRST file per slot.
 */
export const FirmFileSchema = z.object({
  /** Display label — the file's Airtable filename. */
  name: z.string(),
  /** Airtable attachment URL (opens in a new tab). */
  url: z.string().url(),
});
export type FirmFile = z.infer<typeof FirmFileSchema>;

/**
 * The firm's primary point of contact at LegalFlare — enriched server-side
 * from the Contacts table so the panel can render name + email + phone
 * without a follow-up fetch. Null when no primary contact is linked, or when
 * the link points at a deleted contact.
 */
export const FirmPrimaryContactSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  /** `Admin` | `User` | null. */
  role: z.string().nullable(),
});
export type FirmPrimaryContact = z.infer<typeof FirmPrimaryContactSchema>;

/**
 * The firm's admin paperwork. One slot per artefact LF holds on a firm.
 * MFA — Handling is only relevant for firms whose designation includes
 * Handling; the panel hides it on Originating-only firms. W-9 and
 * MFA — Originating are relevant for any firm operating as Originating (LF
 * pays Originating firms — the W-9 is the payee's tax form). Malpractice
 * Insurance applies to every firm (#677).
 */
export const FirmDocumentsSchema = z.object({
  w9: FirmFileSchema.nullable(),
  malpracticeInsurance: FirmFileSchema.nullable(),
  mfaHandling: FirmFileSchema.nullable(),
  mfaOriginating: FirmFileSchema.nullable(),
});
export type FirmDocuments = z.infer<typeof FirmDocumentsSchema>;

// ---------------------------------------------------------------------------
// Intake artifacts (Slice M — ADR-0100)
// ---------------------------------------------------------------------------

/**
 * The six intake artifacts LF tracks per firm — the five document slots plus
 * Primary Contact. Wire keys; the Airtable `Exempt Artifacts` multipleSelects
 * stores the human option names (see `EXEMPT_OPTION_BY_ARTIFACT`).
 */
export const INTAKE_ARTIFACTS = [
  "logo",
  "w9",
  "malpractice",
  "mfaH",
  "mfaO",
  "primaryContact",
] as const;
export const IntakeArtifactSchema = z.enum(INTAKE_ARTIFACTS);
export type IntakeArtifact = z.infer<typeof IntakeArtifactSchema>;

/** Display labels for the Intake Gaps view + panel affordances. */
export const INTAKE_ARTIFACT_LABELS: Record<IntakeArtifact, string> = {
  logo: "Logo",
  w9: "W-9",
  malpractice: "Malpractice Insurance",
  mfaH: "MFA — Handling",
  mfaO: "MFA — Originating",
  primaryContact: "Primary Contact",
};

/**
 * Wire key ↔ Airtable `Exempt Artifacts` option name. The option names were
 * created 2026-06-09 (field `fldJYsL1bGWuZLmzz` on Firms) and operators may
 * also edit them directly in Airtable, so the names are short and human.
 */
export const EXEMPT_OPTION_BY_ARTIFACT: Record<IntakeArtifact, string> = {
  logo: "Logo",
  w9: "W-9",
  malpractice: "Malpractice",
  mfaH: "MFA-H",
  mfaO: "MFA-O",
  primaryContact: "Primary Contact",
};

/** Reverse map — Airtable option name → wire key; null for unknown options
 *  (someone added a new option in Airtable the code doesn't know yet —
 *  ignored rather than crashing the projection). */
export function artifactByExemptOption(option: string): IntakeArtifact | null {
  for (const artifact of INTAKE_ARTIFACTS) {
    if (EXEMPT_OPTION_BY_ARTIFACT[artifact] === option) return artifact;
  }
  return null;
}

/**
 * Which artifacts a firm's designation makes applicable — the single source
 * of truth for the Intake Gaps composer, the designation watcher, and the
 * document upload/delete route. Mirrors ADR-0086's upload designation gate
 * (the chase list must only name gaps the panel can actually close).
 *
 * NOT the panel: `firm-detail-panel.tsx` gates its document rows with its own
 * local `firmHandles`/`firmOriginates` predicates and never imports this. The
 * two must be changed together.
 *
 *   - Logo + Primary Contact + Malpractice — every firm.
 *   - MFA — Handling — firms handling cases (Handling | Both).
 *   - W-9 + MFA — Originating — firms LF pays (Originating | Both). The W-9
 *     is the payee's tax form — an ORIGINATING-side artifact. (The phase-2
 *     plan's § Slice M scope line had this backwards; ADR-0086's live gate
 *     is the authority — recorded in ADR-0100.)
 *
 * Malpractice is universal (Chris, #677: treat every firm the same). It is
 * proof the firm carries coverage, independent of which side of a placement it
 * sits on, and handling firms ask originating firms for it as a condition of
 * co-counsel arrangements on placed cases. So the chase and the upload both
 * reach every firm — an OF with no declarations page on file reads as an intake
 * gap. Per-firm opt-out is `Exempt Artifacts`, not the designation.
 *
 * A firm with no designation gets only the universal artifacts — we can't
 * know which side's paperwork applies until the designation is set.
 */
export function applicableArtifactsFor(
  designation: string | null,
): IntakeArtifact[] {
  const out: IntakeArtifact[] = ["logo", "primaryContact", "malpractice"];
  if (designation === "Handling" || designation === "Both") {
    out.push("mfaH");
  }
  if (designation === "Originating" || designation === "Both") {
    out.push("w9", "mfaO");
  }
  return out;
}

/**
 * One firm — the roster row chrome (name / designation / routing status badges
 * + logo) and the admin info-panel payload (primary contact, documents, the
 * contact / lead / placement counts). Shipped together so the panel opens
 * without a second fetch.
 *
 * The panel is the LF-admin "do we have everything on file?" surface. Coverage
 * (case types / states the firm covers) is NOT here — that is operational data
 * that belongs on a future firm-dedicated page, not the admin panel.
 */
export const ClientSuccessFirmSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** `Originating` | `Handling` | `Both` | null. */
  designation: z.string().nullable(),
  /** `Active` | `Paused` | null. */
  routingStatus: z.string().nullable(),
  /** Logo URL (first Airtable attachment), or null when no logo on file. */
  logoUrl: z.string().url().nullable(),
  notificationsEmail: z.string().nullable(),
  primaryContact: FirmPrimaryContactSchema.nullable(),
  /**
   * True iff Airtable's Primary Contact link held MORE than one record. The
   * panel still surfaces `primaryContact` (the first one) but flags the
   * ambiguity so the operator can clean it up in Airtable.
   */
  primaryContactAmbiguous: z.boolean(),
  documents: FirmDocumentsSchema,
  /**
   * Firm's mailing address (LF-admin-tracked — Originating firms; destination
   * for referral-payout checks). Free-form multi-line text. Always nullable
   * on the wire; the panel gates rendering by designation.
   */
  address: z.string().nullable(),
  /**
   * Firm's email domain(s), one per line (multilineText on Airtable). Read
   * by the Lead Parser n8n workflow for inbound attribution; blank → AI name
   * match fallback. Null when empty or whitespace-only. Also read, through the
   * ops-MCP's `get_firm` (which passes this row through unchanged), by
   * Marketing's CRM link step (`link_lfops.py`), which splits it on commas,
   * semicolons and whitespace — see engineering/ops-mcp/CONTRACT.md, "Callers
   * outside a Claude session", which also names this row's `id` and `name`.
   */
  emailDomains: z.string().nullable(),
  /**
   * Free-form routing-preference notes (LF-admin internal — zero automation
   * consumers per ADR-0091 § Downstream-consumer audit). Null when empty.
   */
  routingPreferences: z.string().nullable(),
  /**
   * ISO-8601 instant the Firm's Airtable record was created (system
   * createdTime). For a Firm created through create_firm that is its setup;
   * a Firm moved into this base at the April 2026 cutover carries the move's
   * instant instead (provenance `migration_2026_04`,
   * engineering/contract/provenance.ts). Read through lf-ops get_firm and
   * list_firms (#1105).
   */
  createdAt: z.string(),
  /**
   * Firms.Open Routing (#916) — an Originating Firm whose every Lead goes to
   * every eligible Handling Firm at once; false when unchecked. What
   * create_firm / correct_firm write as `open_routing`, read back through
   * lf-ops get_firm (#1105).
   */
  openRouting: z.boolean(),
  /**
   * Firms.Originating Firms Only (#916) — the Firms record ids of the
   * Originating Firms this Handling Firm may receive Leads from; empty =
   * unrestricted. What create_firm / correct_firm write as
   * `originating_firms_only`, read back through lf-ops get_firm (#1105).
   */
  originatingFirmsOnly: z.array(z.string()),
  /**
   * Firms.Email-Only Referrals (#1466) — each new referral is emailed to the
   * Firm's Lead/Case Contacts instead of the sign-into-portal email; false
   * when unchecked. What lf-ops correct_firm / create_firm write as
   * `email_only_referrals`, read back through lf-ops get_firm (#1489).
   */
  emailOnlyReferrals: z.boolean(),
  /**
   * Firms.Package Delivery — each new referral goes as a full-detail email
   * package to the Firm's intake inbox; false when unchecked. Set by hand in
   * Airtable; read through lf-ops get_firm (#1489).
   */
  packageDelivery: z.boolean(),
  /**
   * Firms.Mass-tort campaigns (#1455) — the named mass-tort campaigns the
   * Firm takes one-off clients in (the field's choice names; [] when unset).
   * What lf-ops correct_firm writes as `mass_tort_campaigns`, the whole list,
   * read back through lf-ops get_firm (#1483).
   */
  massTortCampaigns: z.array(z.string()),
  contactCount: z.number().int().nonnegative(),
  leadCount: z.number().int().nonnegative(),
  placementCount: z.number().int().nonnegative(),
  /**
   * Live to-dos pointing at this firm (Dashboard Slice I / ADR-0089). All
   * operators' rows, not viewer-filtered — the panel renders the firm's whole
   * outstanding chase queue regardless of who's assigned. Projected through
   * the existing DashboardRow shape so the panel can reuse the Dashboard's row
   * layout; the panel keeps rows read-only (close-loop actions still happen
   * from the Dashboard or via the deep-link).
   */
  tasks: z.array(DashboardRowSchema),
  /**
   * Tasks about this firm that closed (Completed set) within the trailing 7
   * days — short-term memory aid so an operator opening the panel can see
   * what was just handled without scrolling history.
   */
  recentlyClosedTasks: z.array(DashboardRowSchema),
  /**
   * Intake artifacts this firm is exempt from providing (Slice M /
   * ADR-0100). Artifact KEYS on the wire (the Airtable field stores the
   * human option names; the adapter maps). Drives the panel's "Mark not
   * required" / "Restore as required" affordances and the exemptions
   * side-list; the Intake Gaps composer skips exempted artifacts.
   */
  exemptArtifacts: z.array(IntakeArtifactSchema),
  /**
   * Designation-drift alert (Slice M / ADR-0100). Non-null when the firm's
   * Designation changed and artifacts that are NOW applicable sit on the
   * exempt list — the silent-compliance-drift safety net. Resolves when
   * each conflicting exemption is cleared (Restore as required) or
   * re-affirmed (Mark not required again, fresh reason). Null when the
   * designation store is unavailable (no D1) — same surface as pre-M.
   */
  designationAlert: z
    .object({
      prevDesignation: z.string().nullable(),
      conflictingExemptions: z.array(IntakeArtifactSchema),
    })
    .nullable(),
});
export type ClientSuccessFirm = z.infer<typeof ClientSuccessFirmSchema>;

export const ClientSuccessFirmsResponseSchema = z.object({
  firms: z.array(ClientSuccessFirmSchema),
});
export type ClientSuccessFirmsResponse = z.infer<
  typeof ClientSuccessFirmsResponseSchema
>;

/**
 * One user (firm-side Contact). Flat row — the Users view is a table with no
 * detail panel. `firmName` is joined from the Linked Firm record id; null when
 * the contact has no firm or the link is broken.
 *
 * `id`, `name`, `email` and `firmId` of every row are also read, through the
 * ops-MCP's `list_users` (which returns these rows unchanged and unpaged), by
 * Marketing's CRM link step (`link_lfops.py`), which calls `.split` on `email`
 * and matches `firmId` to the Firm row's `id` — see
 * engineering/ops-mcp/CONTRACT.md, "Callers outside a Claude session".
 */
export const ClientSuccessUserSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  /** `Admin` | `User` | null. */
  role: z.string().nullable(),
  /** `Active` | `Inactive` | null. */
  status: z.string().nullable(),
  /**
   * Notification categories (multipleSelects): `Lead/Case` | `Daily Digest`
   * | `Announcements` | `Weekly Report` (#882) | `Placed` | `Settled` (#947).
   * #506 — replaced the retired `Designation`. Empty when none.
   */
  notifications: z.array(z.string()),
  /** Job title (display only), or null. */
  position: z.string().nullable(),
  firmId: z.string().nullable(),
  firmName: z.string().nullable(),
  /**
   * Whether an attorney good-standing certificate is on file — presence only,
   * not the file. The `{name, url}` object stays detail-only
   * (`ClientSuccessUserDetailSchema`): its Airtable URL is signed and expires
   * in hours, and the roster is ~120 rows read far more often than any one
   * certificate is opened. Presence + `goodStandingDate` is what a compliance
   * sweep needs; the file itself is minted fresh on panel open. Issue #636.
   */
  goodStandingOnFile: z.boolean().default(false),
  /**
   * Date the good-standing certificate was last uploaded (ISO `YYYY-MM-DD`),
   * or null when none on file. Carried on the bulk row so an expiry question
   * (`+365d < today`) is answerable across the whole roster in one read
   * without opening each panel. Issue #636.
   */
  goodStandingDate: z.string().nullable().default(null),
});
export type ClientSuccessUser = z.infer<typeof ClientSuccessUserSchema>;

export const ClientSuccessUsersResponseSchema = z.object({
  users: z.array(ClientSuccessUserSchema),
});
export type ClientSuccessUsersResponse = z.infer<
  typeof ClientSuccessUsersResponseSchema
>;

/**
 * One user (firm-side Contact) for the User Detail Panel (Dashboard Slice I /
 * ADR-0090). Extends the flat list shape with the firm's outstanding to-do
 * queue pointed at this user, scoped the same way as a Firm panel — all
 * operators' rows, not viewer-filtered.
 *
 * The Users table is ~120 rows so this is fetched per-id on panel open via
 * `GET /api/firm-success/users/:id`, NOT enriched in the bulk
 * `/api/firm-success/users` payload — keeps the list query light while the
 * panel pays only for what it opens. That split is about work the list would
 * otherwise do per row: `tasks` / `recentlyClosedTasks` need task reads the
 * list never runs. It is NOT a blanket rule — good-standing presence and date
 * ride the bulk row (issue #636), because `listAll()` already reads both
 * fields on every roster fetch and they cost no extra query.
 */
export const ClientSuccessUserDetailSchema = ClientSuccessUserSchema.extend({
  tasks: z.array(DashboardRowSchema),
  recentlyClosedTasks: z.array(DashboardRowSchema),
  /**
   * Attorney good-standing certificate on file (the first attachment), or
   * null when none. Detail-only — the bulk row carries `goodStandingOnFile`
   * (presence) and `goodStandingDate` instead, so a roster-wide compliance
   * sweep needs no panel opens. What stays here is the file itself: its
   * Airtable URL is signed and short-lived, so it is minted on panel open
   * rather than ~120 at a time into a list payload. Issue #416, #636.
   */
  goodStandingFile: FirmFileSchema.nullable().default(null),
});
export type ClientSuccessUserDetail = z.infer<
  typeof ClientSuccessUserDetailSchema
>;

export const ClientSuccessUserDetailResponseSchema = z.object({
  user: ClientSuccessUserDetailSchema,
});
export type ClientSuccessUserDetailResponse = z.infer<
  typeof ClientSuccessUserDetailResponseSchema
>;

// ---------------------------------------------------------------------------
// Attorney good-standing document (issue #416) — the per-user credential.
// ---------------------------------------------------------------------------
// Uploaded / replaced / deleted from the CC user panel (JSON + base64) and the
// Ops MCP `file_document` / `unfile_document` verbs (field Attorney Good
// Standing; the MCP sends { source_url } and the route fetches — #910).
// ONE document per user — no slot system (unlike firm documents): the
// route addresses it at /api/firm-success/users/:id/good-standing. A
// companion `Good Standing Date` is stamped server-side on upload (drives the
// ~12-month re-ask) and cleared on delete.

/**
 * Request body for POST /api/firm-success/users/:id/good-standing from the
 * web panel — the file inline as base64 (the panel reads the File to base64
 * client-side). The route applies the registry's type list, decodes the
 * base64 and hands the bytes to the portal's document door (#974). PDF or image.
 */
export const AttorneyGoodStandingUploadJsonRequestSchema = z.object({
  filename: z.string().min(1).max(255),
  contentType: z.string().min(1).max(255),
  contentBase64: z.string().min(1),
});
export type AttorneyGoodStandingUploadJsonRequest = z.infer<
  typeof AttorneyGoodStandingUploadJsonRequestSchema
>;

/**
 * Request body for POST /api/firm-success/users/:id/good-standing from the
 * Ops MCP (`file_document`, field Attorney Good Standing — #910): a fetchable
 * https URL; the route fetches the bytes server-side, applies the same MIME
 * allowlist and size cap, refuses an occupied slot (409 slot_occupied — unfile
 * first), and hands base64 to the same writer. No byte crosses the MCP.
 */
export const AttorneyGoodStandingFileRequestSchema = z.object({
  source_url: z.string().url().max(2048),
  filename: z.string().min(1).max(255).optional(),
});
export type AttorneyGoodStandingFileRequest = z.infer<
  typeof AttorneyGoodStandingFileRequestSchema
>;

/**
 * Echo from POST / DELETE /api/firm-success/users/:id/good-standing — the
 * resulting file (null after a delete) and the stamped date (null after a
 * delete). The SPA's mutation hook patches the cached user-detail's
 * `goodStandingFile` / `goodStandingDate` without a re-projection roundtrip.
 */
export const AttorneyGoodStandingMutationResponseSchema = z.object({
  id: z.string(),
  file: FirmFileSchema.nullable(),
  date: z.string().nullable(),
});
export type AttorneyGoodStandingMutationResponse = z.infer<
  typeof AttorneyGoodStandingMutationResponseSchema
>;

/**
 * Echo from POST /api/firm-success/firms/:id/pause and …/unpause — the
 * minimum needed to flip the badge. Only `routingStatus` changes on a global
 * pause/unpause; coverage, counts, designation, and the rest of the panel
 * payload are unchanged, so the SPA's mutation hook patches the matching
 * firm's `routingStatus` in the firms-list cache without a re-projection
 * roundtrip. See `FirmWriter` in `@command-center/adapter-airtable` for the
 * downstream blast-radius (auto-router + manual pickers + Lead Sent).
 */
export const FirmPauseResponseSchema = z.object({
  id: z.string(),
  routingStatus: z.enum(["Active", "Paused"]),
});
export type FirmPauseResponse = z.infer<typeof FirmPauseResponseSchema>;

/**
 * Document slot identifier — the URL path parameter and the wire-shape key
 * the panel uses to address a single artefact on a firm. One of:
 *   - `logo` — the firm's logo (all firms).
 *   - `w9` — W-9 form (Originating firms).
 *   - `malpractice` — proof of malpractice insurance (all firms; #677).
 *   - `mfaH` — Master Fee Agreement for this firm as Handling.
 *   - `mfaO` — Master Fee Agreement for this firm as Originating.
 * See ADR-0086 for the full contract (designation gate, replace semantics,
 * MIME allowlist, size cap).
 */
export const FirmDocumentSlotSchema = z.enum([
  "logo",
  "w9",
  "malpractice",
  "mfaH",
  "mfaO",
]);
export type FirmDocumentSlot = z.infer<typeof FirmDocumentSlotSchema>;

/**
 * Echo from POST /api/firm-success/firms/:id/documents/:slot (upload) and
 * DELETE …/documents/:slot — the slot identifier and the resulting file (or
 * null after a delete). The SPA's mutation hook patches the matching firm's
 * `logoUrl` (when slot=`logo`) or `documents.<key>` in the firms-list cache
 * without re-fetching the projection.
 */
export const FirmDocumentMutationResponseSchema = z.object({
  id: z.string(),
  slot: FirmDocumentSlotSchema,
  file: FirmFileSchema.nullable(),
});
export type FirmDocumentMutationResponse = z.infer<
  typeof FirmDocumentMutationResponseSchema
>;

/**
 * Request body for the JSON variant of POST
 * /api/firm-success/firms/:id/documents/:slot — the Ops MCP path
 * (`file_document`, a firm slot — #910; until then `upload_firm_document` /
 * `attach_firm_document` carried the file inline as base64). The web panel
 * posts multipart/form-data with a File; the MCP sends a fetchable https URL
 * and the route fetches the bytes server-side — no byte crosses the MCP. Same
 * downstream contract — the identical designation gate and type list run on
 * the fetched bytes before they go to the portal's document door (#974) — plus
 * one rule the multipart panel path does not have: an occupied slot is
 * refused (409 slot_occupied, naming the occupant and unfile_document); the
 * panel keeps replace-on-upload. The route selects this shape when
 * Content-Type is application/json.
 */
export const FirmDocumentFileRequestSchema = z.object({
  source_url: z.string().url().max(2048),
  filename: z.string().min(1).max(255).optional(),
});
export type FirmDocumentFileRequest = z.infer<
  typeof FirmDocumentFileRequestSchema
>;

/**
 * Request body for PATCH /api/firm-success/firms/:id/address — set or clear
 * the Originating-side mailing address. Trimmed server-side; whitespace-only
 * collapses to null. Capped at 500 characters (covers US + international
 * addresses with headroom; raise if real use pinches).
 */
export const FirmAddressUpdateRequestSchema = z.object({
  address: z.string().max(500).nullable(),
});
export type FirmAddressUpdateRequest = z.infer<
  typeof FirmAddressUpdateRequestSchema
>;

/**
 * Echo from PATCH /api/firm-success/firms/:id/address — id + the resulting
 * address (null after a clear). Web hook patches the cached firm's `address`
 * field without a re-projection roundtrip.
 */
export const FirmAddressUpdateResponseSchema = z.object({
  id: z.string(),
  address: z.string().nullable(),
});
export type FirmAddressUpdateResponse = z.infer<
  typeof FirmAddressUpdateResponseSchema
>;

// ---------------------------------------------------------------------------
// Firm field edits (ADR-0091 — Firm administration in Command Center)
// ---------------------------------------------------------------------------
// PR A of ADR-0091 wires inline edit for five fields on the firm panel. Each
// pair mirrors the FirmAddressUpdate{Request,Response} shape:
//   - Firm Name + Designation are server-required (non-null, non-blank).
//   - Notifications Email + Email Domains + Routing Preferences are nullable
//     (null = clear). Server trims; whitespace-only collapses to null.
// Designation is enum-validated server-side; the panel guards client-side too
// (dropdown of the same enum) but never trusts the client.

/** Designation enum — Airtable singleSelect choices on Firms.Designation. */
export const FirmDesignationSchema = z.enum([
  "Originating",
  "Handling",
  "Both",
]);
export type FirmDesignation = z.infer<typeof FirmDesignationSchema>;

/**
 * Request body for PATCH /api/firm-success/firms/:id/name — set the firm's
 * display name. Server trims; rejects empty/whitespace with 400. No max length
 * enforced at the schema layer (Airtable singleLineText already caps).
 */
export const FirmNameUpdateRequestSchema = z.object({
  name: z.string().min(1),
});
export type FirmNameUpdateRequest = z.infer<typeof FirmNameUpdateRequestSchema>;

export const FirmNameUpdateResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type FirmNameUpdateResponse = z.infer<
  typeof FirmNameUpdateResponseSchema
>;

/**
 * Request body for PATCH /api/firm-success/firms/:id/designation — set the
 * firm's designation. Enum-validated (one of Originating | Handling | Both).
 * The cascade (panel show/hide of document slots, auto-router eligibility) is
 * documented in ADR-0091 § Designation cascade.
 */
export const FirmDesignationUpdateRequestSchema = z.object({
  designation: FirmDesignationSchema,
});
export type FirmDesignationUpdateRequest = z.infer<
  typeof FirmDesignationUpdateRequestSchema
>;

export const FirmDesignationUpdateResponseSchema = z.object({
  id: z.string(),
  designation: FirmDesignationSchema,
});
export type FirmDesignationUpdateResponse = z.infer<
  typeof FirmDesignationUpdateResponseSchema
>;

/**
 * Request body for PATCH /api/firm-success/firms/:id/notifications-email —
 * set or clear the firm's Notifications Email. Capped at 320 characters (RFC
 * 5321 email-length cap). Server trims; whitespace-only collapses to null.
 */
export const FirmNotificationsEmailUpdateRequestSchema = z.object({
  notificationsEmail: z.string().max(320).nullable(),
});
export type FirmNotificationsEmailUpdateRequest = z.infer<
  typeof FirmNotificationsEmailUpdateRequestSchema
>;

export const FirmNotificationsEmailUpdateResponseSchema = z.object({
  id: z.string(),
  notificationsEmail: z.string().nullable(),
});
export type FirmNotificationsEmailUpdateResponse = z.infer<
  typeof FirmNotificationsEmailUpdateResponseSchema
>;

/**
 * Request body for PATCH /api/firm-success/firms/:id/email-domains — set or
 * clear the firm's Email Domains (multilineText, one domain per line). Capped
 * at 2000 characters (covers dozens of domains with headroom). Server trims;
 * whitespace-only collapses to null.
 */
export const FirmEmailDomainsUpdateRequestSchema = z.object({
  emailDomains: z.string().max(2000).nullable(),
});
export type FirmEmailDomainsUpdateRequest = z.infer<
  typeof FirmEmailDomainsUpdateRequestSchema
>;

export const FirmEmailDomainsUpdateResponseSchema = z.object({
  id: z.string(),
  emailDomains: z.string().nullable(),
});
export type FirmEmailDomainsUpdateResponse = z.infer<
  typeof FirmEmailDomainsUpdateResponseSchema
>;

/**
 * Request body for PATCH /api/firm-success/firms/:id/routing-preferences —
 * set or clear the firm's Routing Preferences (free-form internal notes; zero
 * automation consumers per ADR-0091). Capped at 4000 characters. Server trims;
 * whitespace-only collapses to null.
 */
export const FirmRoutingPreferencesUpdateRequestSchema = z.object({
  routingPreferences: z.string().max(4000).nullable(),
});
export type FirmRoutingPreferencesUpdateRequest = z.infer<
  typeof FirmRoutingPreferencesUpdateRequestSchema
>;

export const FirmRoutingPreferencesUpdateResponseSchema = z.object({
  id: z.string(),
  routingPreferences: z.string().nullable(),
});
export type FirmRoutingPreferencesUpdateResponse = z.infer<
  typeof FirmRoutingPreferencesUpdateResponseSchema
>;

// ---------------------------------------------------------------------------
// Intake Gaps (Slice M — ADR-0100)
// ---------------------------------------------------------------------------

/**
 * One firm inside a gap group. `gapAgeDays` is days since the FIRM was
 * created — the honest proxy for gap age (the system does not record when a
 * document went missing; for the chase-priority question "who has waited
 * longest" firm age is the derivable answer). Sorted descending in each
 * group, so the first three ARE the monthly post's "top-3 oldest".
 */
export const IntakeGapFirmSchema = z.object({
  id: z.string(),
  name: z.string(),
  designation: z.string().nullable(),
  gapAgeDays: z.number().int().nonnegative(),
});
export type IntakeGapFirm = z.infer<typeof IntakeGapFirmSchema>;

/** One gap group — every firm missing this artifact (post designation gate,
 *  exemption filter, and new-firm grace). Groups with zero firms still ship
 *  so the view can render truthful zero counts (Slice G posture). */
export const IntakeGapSchema = z.object({
  artifact: IntakeArtifactSchema,
  firms: z.array(IntakeGapFirmSchema),
});
export type IntakeGap = z.infer<typeof IntakeGapSchema>;

/**
 * GET /api/firm-success/intake-gaps payload. `onboarding` carries firms
 * younger than 14 days that have gaps — rendered as a separate section,
 * excluded from the monthly Slack post (ADR-0100 locked decision 3).
 */
export const IntakeGapsResponseSchema = z.object({
  gaps: z.array(IntakeGapSchema),
  onboarding: z.object({
    firms: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        ageDays: z.number().int().nonnegative(),
        /** The artifacts this onboarding firm is still missing. */
        missing: z.array(IntakeArtifactSchema),
      }),
    ),
  }),
  /** ISO-8601 — when the worker composed this payload. */
  generatedAt: z.string(),
});
export type IntakeGapsResponse = z.infer<typeof IntakeGapsResponseSchema>;

/**
 * POST /api/firm-success/firms/:id/exemption — mark an artifact not
 * required (free-text reason REQUIRED, ADR-0100 locked decision 2) or
 * restore it as required. Replace-style semantics server-side: the route
 * computes the firm's new exempt set and writes it whole.
 */
export const FirmExemptionRequestSchema = z.discriminatedUnion("action", [
  z.object({
    action: z.literal("mark"),
    artifact: IntakeArtifactSchema,
    reason: z.string().min(1).max(1000),
  }),
  z.object({
    action: z.literal("clear"),
    artifact: IntakeArtifactSchema,
  }),
]);
export type FirmExemptionRequest = z.infer<typeof FirmExemptionRequestSchema>;

/** Echo — the firm's full exempt set after the write (artifact keys). The
 *  SPA patches the cached firm's `exemptArtifacts` without a re-fetch. */
export const FirmExemptionResponseSchema = z.object({
  id: z.string(),
  exemptArtifacts: z.array(IntakeArtifactSchema),
});
export type FirmExemptionResponse = z.infer<typeof FirmExemptionResponseSchema>;

// ---------------------------------------------------------------------------
// Client comms templates (Ops MCP T3 #8 — gap-closing)
// ---------------------------------------------------------------------------
// POST /api/firm-success/firms/:id/comms-templates/apply takes NO request
// body — it promotes the firm's `Pending Comms Review` blob (written by the
// portal's firm-templates submit path) to the live template fields and clears
// the staging fields. The blob is a SPARSE diff: only the template keys and
// sub-fields the firm changed are present. This schema validates that blob
// after JSON.parse; the route rejects a structurally-bad or empty blob (422).
//
// PATCH /api/firm-success/firms/:id/client-comms-enabled is a direct set of
// the firm-wide master toggle — it never flows through the staging path, so it
// has its own tiny request body.

/** One template's promotable fields — all optional (sparse diff). */
export const CommsTemplatePatchSchema = z.object({
  subject: z.string().optional(),
  body: z.string().optional(),
  cc: z.string().optional(),
  disabled: z.boolean().optional(),
});
export type CommsTemplatePatch = z.infer<typeof CommsTemplatePatchSchema>;

/**
 * The `Pending Comms Review` JSON blob shape. Refined to reject an empty blob
 * (no template carrying any field) so the apply route never promotes nothing
 * and clears a firm's pending queue for free.
 */
export const PendingCommsReviewSchema = z
  .object({
    templates: z.object({
      intro: CommsTemplatePatchSchema.optional(),
      update: CommsTemplatePatchSchema.optional(),
      accepted: CommsTemplatePatchSchema.optional(),
      rejection: CommsTemplatePatchSchema.optional(),
    }),
  })
  .refine(
    (v) =>
      [
        v.templates.intro,
        v.templates.update,
        v.templates.accepted,
        v.templates.rejection,
      ].some(
        (t) =>
          t !== undefined &&
          (t.subject !== undefined ||
            t.body !== undefined ||
            t.cc !== undefined ||
            t.disabled !== undefined),
      ),
    { message: "pending comms blob has no applicable template fields" },
  );
export type PendingCommsReview = z.infer<typeof PendingCommsReviewSchema>;

/** Echo from the apply route — id + the `key.field` paths written to live. */
export const FirmCommsTemplatesApplyResponseSchema = z.object({
  id: z.string(),
  applied: z.array(z.string()),
});
export type FirmCommsTemplatesApplyResponse = z.infer<
  typeof FirmCommsTemplatesApplyResponseSchema
>;

/** Request body for PATCH …/client-comms-enabled — the master toggle. */
export const FirmClientCommsEnabledUpdateRequestSchema = z.object({
  enabled: z.boolean(),
});
export type FirmClientCommsEnabledUpdateRequest = z.infer<
  typeof FirmClientCommsEnabledUpdateRequestSchema
>;

export const FirmClientCommsEnabledUpdateResponseSchema = z.object({
  id: z.string(),
  clientCommsEnabled: z.boolean(),
});
export type FirmClientCommsEnabledUpdateResponse = z.infer<
  typeof FirmClientCommsEnabledUpdateResponseSchema
>;

// ---------------------------------------------------------------------------
// Client-Email Review (ADR-0115) — GET …/firms/:id/comms-templates
// ---------------------------------------------------------------------------
// The read behind the Command Center review-and-approve surface (and the Ops
// MCP `get_firm_comms_templates` verb). Per email it carries three states —
// the LF `standard` (master-firm wording), the firm's `live` wording, and the
// firm's `pending` sparse proposal (null when nothing staged) — plus the
// fields that changed, so the page can badge a card CHANGED and show
// standard-vs-proposed without a word-diff library. Approve flows through the
// existing apply route; this schema is read-only.

/** The four reactive client-email templates, in send-lifecycle order. */
export const CommsTemplateKeySchema = z.enum([
  "intro",
  "update",
  "accepted",
  "rejection",
]);
export type CommsTemplateKey = z.infer<typeof CommsTemplateKeySchema>;

/** A template's sub-fields. */
export const CommsTemplateFieldKeySchema = z.enum([
  "subject",
  "body",
  "cc",
  "disabled",
]);
export type CommsTemplateFieldKey = z.infer<typeof CommsTemplateFieldKeySchema>;

/** A template's fully-projected field set (live / standard). */
export const CommsTemplateFieldsSchema = z.object({
  subject: z.string(),
  body: z.string(),
  cc: z.string(),
  disabled: z.boolean(),
});
export type CommsTemplateFields = z.infer<typeof CommsTemplateFieldsSchema>;

/** One email's review diff. */
export const CommsTemplateDiffSchema = z.object({
  key: CommsTemplateKeySchema,
  standard: CommsTemplateFieldsSchema,
  live: CommsTemplateFieldsSchema,
  /** The firm's proposed change (sparse), or null when nothing is pending. */
  pending: CommsTemplatePatchSchema.nullable(),
  /** Sub-fields whose pending value differs from live. */
  changedFields: z.array(CommsTemplateFieldKeySchema),
  /** True when `changedFields` is non-empty. */
  changed: z.boolean(),
});
export type CommsTemplateDiff = z.infer<typeof CommsTemplateDiffSchema>;

export const FirmCommsTemplatesViewResponseSchema = z.object({
  id: z.string(),
  firmName: z.string(),
  /** `Handling` | `Originating` | `Both` | null — the UI gates entry to OF/Both. */
  designation: z.string().nullable(),
  /** Firm-wide master gate — whether client emails send at all. */
  clientCommsEnabled: z.boolean(),
  /** True when a valid pending submission exists (drives the Approve button). */
  hasPending: z.boolean(),
  /** When the pending submission was made (raw Airtable date string), or null. */
  submittedAt: z.string().nullable(),
  /** Email of the firm user who submitted, or null. */
  submittedBy: z.string().nullable(),
  /** One diff per email, in intro/update/accepted/rejection order. */
  templates: z.array(CommsTemplateDiffSchema),
});
export type FirmCommsTemplatesViewResponse = z.infer<
  typeof FirmCommsTemplatesViewResponseSchema
>;
